// Lógica pura de la vista "Log importación nóminas"
// (pages/dashboard/personal/empleados/log-importacion-nominas.vue), que consume
// GET /payroll-import-logs/:page y GET /status-payroll-import-logs de la API
// (ver api/CLAUDE.md, "Import runner, log + Slack").
//
// Los items traen nombres de fichero (= datos personales): este módulo solo los
// transforma para pintarlos a admins, nunca los registra.

/** Link del Form de la vista (catálogo del seeder de la API, Form 54). */
export const PAYROLL_IMPORT_LOG_LINK = "personal/empleados/log-importacion-nominas";

const MONTHS = [
	"Enero",
	"Febrero",
	"Marzo",
	"Abril",
	"Mayo",
	"Junio",
	"Julio",
	"Agosto",
	"Septiembre",
	"Octubre",
	"Noviembre",
	"Diciembre",
];

const ORIGIN_LABELS = {
	manual: "Manual",
	cron: "Automática (diaria)",
	external: "Sistema externo",
};

// Orígenes que puede producir cada modo (native: botón/CLI + cron; external:
// POST /external-payrolls/).
const ORIGINS_BY_MODE = {
	native: ["manual", "cron"],
	external: ["external"],
};

// Motivos estables de domain.Reason (api/.../payrollimport/domain/result.go).
const REASON_LABELS = {
	empty_file_name: "Nombre de fichero vacío",
	not_pdf: "El fichero no es un PDF",
	missing_agency_separator: 'Nombre de fichero sin código de gestoría (falta "_")',
	missing_name_comma: 'Nombre de fichero sin coma entre apellidos y nombre',
	empty_employee_name: "Nombre de fichero sin nombre de empleado",
	no_employee_match: "Ningún empleado coincide con el nombre del fichero",
	ambiguous_match:
		"Varios empleados pueden ser los del fichero: rellena el código de gestoría en sus fichas (se reintentará)",
	agency_code_name_mismatch:
		"El código de gestoría es de otro empleado: revisa el código en las fichas (se reintentará)",
	employee_lookup_failed: "Error al buscar el empleado (se reintentará)",
	pdf_protect_failed: "No se pudo proteger el PDF",
	storage_failed: "No se pudo guardar el PDF (se reintentará)",
	persistence_failed: "No se pudo registrar la nómina (se reintentará)",
	internal_error: "Error interno (se reintentará)",
	mail_unreadable: "No se pudo leer el correo",
	mail_without_zip: "El correo no trae ningún ZIP",
	zip_invalid: "El adjunto no es un ZIP válido",
	zip_encrypted: "El ZIP está cifrado y no hay contraseña configurada",
	zip_wrong_password: "La contraseña del ZIP no es correcta",
	zip_too_large: "El ZIP supera el tamaño o el número de ficheros permitidos",
	zip_entry_too_large: "Un fichero del ZIP supera el tamaño permitido",
	zip_entry_unsafe_name: "Un fichero del ZIP tiene un nombre no seguro",
	zip_entry_invalid: "Un fichero del ZIP está dañado",
};

// Fallos a nivel de ejecución de domain.RunFailure.
const RUN_FAILURE_LABELS = {
	source_unavailable: "No se pudo leer el buzón o la fuente de nóminas",
	archive_failed: "No se pudieron archivar los correos (se reprocesarán)",
	import_in_progress: "Ya había otra importación en curso",
	internal_error: "Error interno de la importación",
};

const labelOf = (labels, code) => {
	if (!code) return "";
	return Object.prototype.hasOwnProperty.call(labels, code) ? labels[code] : code;
};

/**
 * ¿Existe la vista del log con este modo? Mismo criterio que las rutas del log en
 * la API (`IsEnabled()`: native o external).
 * @param {string} mode - features.payrollImport.mode (ver features-enabled.js).
 * @returns {boolean}
 */
export const isPayrollImportLogAvailable = (mode) => Object.prototype.hasOwnProperty.call(ORIGINS_BY_MODE, mode);

/**
 * ¿Hay que ocultar este enlace del menú? Solo el del log, y solo si el modo no lo
 * habilita (cubre tenants ya sembrados que pasan a `off`).
 * @param {string} link - Form.link del menú.
 * @param {string} mode - modo de importación del tenant.
 * @returns {boolean}
 */
export const isPayrollImportLogLinkHidden = (link, mode) =>
	link === PAYROLL_IMPORT_LOG_LINK && !isPayrollImportLogAvailable(mode);

/**
 * Periodo de la nómina ("YYYY-MM") como en el título de la nómina ("Agosto 2026").
 * @param {string} period
 * @returns {string} la etiqueta; el valor tal cual si no es válido; "" si falta.
 */
export const formatPayrollPeriod = (period) => {
	if (!period) return "";
	const match = /^(\d{4})-(\d{2})$/.exec(period);
	const month = match ? Number(match[2]) : 0;
	if (month < 1 || month > 12) return period;
	return `${MONTHS[month - 1]} ${match[1]}`;
};

/**
 * @param {string} reason - motivo estable de un fallo (p.ej. "no_employee_match").
 * @returns {string} texto legible; el código si es desconocido; "" si falta.
 */
export const payrollImportReasonLabel = (reason) => labelOf(REASON_LABELS, reason);

/**
 * @param {string} runFailure - fallo de ejecución (p.ej. "source_unavailable").
 * @returns {string} texto legible; el código si es desconocido; "" si no hay fallo.
 */
export const payrollImportRunFailureLabel = (runFailure) => labelOf(RUN_FAILURE_LABELS, runFailure);

/**
 * Opciones del filtro de origen para el modo (la API no tiene endpoint de orígenes).
 * @param {string} mode
 * @returns {{ID: string, origin: string}[]} lista nueva en cada llamada.
 */
export const payrollImportOriginOptions = (mode) =>
	(isPayrollImportLogAvailable(mode) ? ORIGINS_BY_MODE[mode] : []).map((ID) => ({ ID, origin: ORIGIN_LABELS[ID] }));

/**
 * Filtros de la vista para FilterDialog (la clave `filter` es el query param de la
 * API). Estado y origen solo se pintan con 2+ opciones: con una sola, filtrar no
 * reduce nada (p.ej. en modo external todos los intentos son externos).
 * @param {{mode: string, statuses?: {ID: string, statusPayrollImportLog: string}[]}} opts
 *   statuses = respuesta de GET /status-payroll-import-logs.
 * @returns {object[]}
 */
export const buildPayrollImportLogFilters = ({ mode, statuses }) => {
	const filters = [{ label: "Fecha del intento", text: "Fecha del intento", type: "range", filter: "dates" }];
	const statusItems = Array.isArray(statuses) ? statuses : [];
	if (statusItems.length > 1) {
		filters.push({
			label: "Estado",
			filter: "status",
			type: "select",
			text: "statusPayrollImportLog",
			value: "ID",
			items: statusItems,
		});
	}
	const origins = payrollImportOriginOptions(mode);
	if (origins.length > 1) {
		filters.push({ label: "Origen", filter: "origin", type: "select", text: "origin", value: "ID", items: origins });
	}
	return filters;
};

/**
 * Adapta los intentos de la API a filas de la tabla: añade `ID` (la tabla expande
 * por `item.ID` y la API usa `id`) y las etiquetas de estado, origen, periodo, fallo
 * de ejecución y motivo de cada fallo. No muta la entrada.
 * @param {object[]} items - `items` de GET /payroll-import-logs/:page.
 * @param {{ID: string, statusPayrollImportLog: string}[]} [statuses] - etiquetas de estado.
 * @returns {object[]}
 */
export const toPayrollImportLogRows = (items, statuses) => {
	if (!Array.isArray(items)) return [];
	const statusLabels = Object.fromEntries(
		(Array.isArray(statuses) ? statuses : []).map((s) => [s.ID, s.statusPayrollImportLog]),
	);
	return items.map((item) => ({
		...item,
		ID: item.id,
		statusLabel: labelOf(statusLabels, item.status),
		originLabel: labelOf(ORIGIN_LABELS, item.origin),
		periodLabel: formatPayrollPeriod(item.period),
		runFailureLabel: payrollImportRunFailureLabel(item.run_failure),
		failures: (item.failures || []).map((failure) => ({
			...failure,
			reasonLabel: payrollImportReasonLabel(failure.reason),
		})),
	}));
};
